import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "../risikoanalyseStep/tokens";
import { VERTRAGSARTEN, type VertragsartKey } from "./types";

const useStyles = makeStyles({
  grid: {
    display: "grid",
    gridTemplateColumns: "1fr",
    gap: "12px",
    "@media (min-width: 600px)": {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
  },
  tile: {
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.cardBg,
    backgroundImage: "none",
    color: BRAND.ink,
    fontFamily: BRAND.fontPrimary,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "flex-start",
    rowGap: "10px",
    paddingTop: "20px",
    paddingBottom: "18px",
    paddingLeft: "12px",
    paddingRight: "12px",
    minHeight: "160px",
    textAlign: "center",
    ...shorthands.border("2px", "solid", BRAND.border),
    ...shorthands.borderRadius("12px"),
    transitionProperty: "border-color, box-shadow, transform, background-color",
    transitionDuration: "140ms",
    ":hover": {
      ...shorthands.borderColor("rgba(230,106,44,0.6)"),
      transform: "translateY(-2px)",
    },
  },
  tileActive: {
    ...shorthands.borderColor(BRAND.pillarOrange),
    backgroundColor: "rgba(230,106,44,0.06)",
    boxShadow: "0 0 0 4px rgba(230,106,44,0.18)",
  },
  iconWrap: {
    color: BRAND.pillarOrange,
    width: "72px",
    height: "56px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  title: { fontSize: "1.05rem", fontWeight: 700, color: BRAND.ink, lineHeight: 1.2 },
  desc: { fontSize: "0.78rem", color: BRAND.muted, lineHeight: 1.3 },
});

const Svg = ({ children }: { children: React.ReactNode }) => (
  <svg
    width="72"
    height="56"
    viewBox="0 0 72 56"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    {children}
  </svg>
);

const Icons: Record<VertragsartKey, () => JSX.Element> = {
  einzel: () => (
    <Svg>
      <rect x="20" y="6" width="32" height="44" rx="3" />
      <path d="M28 18h16M28 26h14M28 34h16" />
      <circle cx="44" cy="42" r="6" fill="currentColor" stroke="none" opacity="0.85" />
      <text x="44" y="45" textAnchor="middle" fontFamily="Arial" fontSize="9" fontWeight="800" fill="#fff">1</text>
    </Svg>
  ),
  sammel: () => (
    <Svg>
      <rect x="6" y="6" width="22" height="44" rx="3" />
      <path d="M12 18h10M12 26h10M12 34h10" />
      <path d="M30 28h8M38 28l-4-4M38 28l-4 4" />
      <rect x="40" y="14" width="26" height="28" rx="2" />
      <path d="M40 22h26M40 30h26M53 14v28" />
    </Svg>
  ),
  buendel: () => (
    <Svg>
      <rect x="6" y="6" width="32" height="40" rx="3" />
      <rect x="20" y="14" width="32" height="40" rx="3" fill={BRAND.white} />
      <path d="M28 24h16M28 32h14M28 40h16" />
      <circle cx="50" cy="14" r="7" fill="currentColor" stroke="none" opacity="0.9" />
      <text x="50" y="17" textAnchor="middle" fontFamily="Arial" fontSize="10" fontWeight="800" fill="#fff">N</text>
    </Svg>
  ),
};

type Props = {
  value: VertragsartKey | "";
  onChange: (value: VertragsartKey) => void;
};

const VertragsartPicker = ({ value, onChange }: Props) => {
  const styles = useStyles();
  return (
    <div className={styles.grid}>
      {VERTRAGSARTEN.map((t) => {
        const Render = Icons[t.key];
        const active = value === t.key;
        return (
          <button
            key={t.key}
            type="button"
            onClick={() => onChange(t.key)}
            className={mergeClasses(styles.tile, active && styles.tileActive)}
            aria-pressed={active}
          >
            <span className={styles.iconWrap}>
              <Render />
            </span>
            <span className={styles.title}>{t.label}</span>
            <span className={styles.desc}>{t.description}</span>
          </button>
        );
      })}
    </div>
  );
};

export default VertragsartPicker;
